import { useEffect, useState, useMemo } from "react";
import { Link, Routes, Route, NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingBag,
  Users,
  FileText,
  Settings,
  Star,
  Shield,
  Search,
  Plus,
  Trash2,
  Pencil,
  X,
  ArrowRight,
  Download,
  Sparkles,
  Copy,
  LogOut,
  ExternalLink,
} from "lucide-react";
import {
  apiFetch,
  API,
  money,
  mainImage,
  resolveImageUrl,
} from "../lib/api";
import ImageUploader from "../components/ImageUploader";

function Guard({ user, min = "staff", children }) {
  const rank = (r) => ({ owner: 100, admin: 80, staff: 40 }[r] || 0);

  if (!user) {
    return (
      <main className="page auth">
        <div className="auth-card">
          <h1>Admin sign-in required.</h1>
          <p className="sub">
            Please <Link to="/login">sign in</Link> with an admin account.
          </p>
        </div>
      </main>
    );
  }

  if (rank(user.role) < rank(min)) {
    return (
      <main className="page auth">
        <div className="auth-card">
          <h1>Access denied</h1>
          <p className="sub">
            Your account doesn't have permission for this area.
          </p>
        </div>
      </main>
    );
  }

  return children;
}

export default function AdminApp({ user, logout }) {
  const canManageSeo = user?.role === "owner" || user?.role === "admin";

  return (
    <Guard user={user} min="staff">
      <div className="admin-shell">
        <aside className="admin-aside">
          <div className="admin-brand">
            <Link to="/" className="logo-mini">
              <span className="logo-mark" aria-hidden>
                Рюд
              </span>{" "}
              GMK
            </Link>
            <small>{user?.role}</small>
          </div>

          <nav>
            <NavLink
              to="/admin"
              end
              data-testid="admin-nav-dashboard"
            >
              <LayoutDashboard size={16} /> Dashboard
            </NavLink>

            <NavLink
              to="/admin/products"
              data-testid="admin-nav-products"
            >
              <Package size={16} /> Products
            </NavLink>

            <NavLink
              to="/admin/categories"
              data-testid="admin-nav-categories"
            >
              <FolderTree size={16} /> Categories
            </NavLink>

            <NavLink
              to="/admin/orders"
              data-testid="admin-nav-orders"
            >
              <ShoppingBag size={16} /> Orders
            </NavLink>

            <NavLink
              to="/admin/customers"
              data-testid="admin-nav-customers"
            >
              <Users size={16} /> Customers
            </NavLink>

            <NavLink
              to="/admin/reviews"
              data-testid="admin-nav-reviews"
            >
              <Star size={16} /> Reviews
            </NavLink>

            <NavLink
              to="/admin/content"
              data-testid="admin-nav-content"
            >
              <FileText size={16} /> Content pages
            </NavLink>

            {canManageSeo && (
              <NavLink
                to="/admin/seo"
                data-testid="admin-nav-seo"
              >
                <Search size={16} /> SEO Manager
              </NavLink>
            )}

            <NavLink
              to="/admin/settings"
              data-testid="admin-nav-settings"
            >
              <Settings size={16} /> Site settings
            </NavLink>

            {user?.role === "owner" && (
              <NavLink
                to="/admin/admins"
                data-testid="admin-nav-admins"
              >
                <Shield size={16} /> Admins
              </NavLink>
            )}
          </nav>

          <div className="admin-foot">
            <Link
              to="/"
              className="link"
              data-testid="admin-view-site"
            >
              <ExternalLink size={14} /> View site
            </Link>

            <button
              onClick={logout}
              className="link"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        <main className="admin-main">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="categories" element={<Categories />} />
            <Route path="orders" element={<Orders />} />
            <Route path="customers" element={<Customers />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="content" element={<ContentPages />} />

            {canManageSeo && (
              <Route path="seo" element={<SEOManager />} />
            )}

            <Route path="settings" element={<SiteSettings />} />

            {user?.role === "owner" && (
              <Route path="admins" element={<Admins />} />
            )}
          </Routes>
        </main>
      </div>
    </Guard>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    apiFetch("/dashboard/stats")
      .then(setStats)
      .catch(() => {});

    apiFetch("/activity-log")
      .then(setActivity)
      .catch(() => {});
  }, []);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">Overview</span>
          <h1>Dashboard</h1>
        </div>
      </header>

      {stats && (
        <>
          <div className="stat-grid">
            <Stat
              label="Sales today"
              value={money(stats.sales_today)}
            />
            <Stat
              label="This week"
              value={money(stats.sales_week)}
            />
            <Stat
              label="This month"
              value={money(stats.sales_month)}
            />
            <Stat
              label="Products"
              value={stats.total_products}
            />
            <Stat
              label="Customers"
              value={stats.total_customers}
            />
          </div>

          <div className="admin-split">
            <div className="admin-card">
              <h3>Orders by status</h3>

              <ul className="status-list">
                {Object.entries(stats.orders_by_status).map(
                  ([k, v]) => (
                    <li key={k}>
                      <span>{k}</span>
                      <b>{v}</b>
                    </li>
                  )
                )}
              </ul>
            </div>

            <div className="admin-card">
              <h3>Low stock</h3>

              {!stats.low_stock.length && (
                <p className="muted">All stocked.</p>
              )}

              {stats.low_stock.map((p) => (
                <div className="row" key={p.id}>
                  <span>{p.name}</span>
                  <b
                    className={
                      p.stock === 0 ? "danger" : "warn"
                    }
                  >
                    {p.stock}
                  </b>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-card">
            <h3>Recent activity</h3>

            {!activity.length && (
              <p className="muted">No activity yet.</p>
            )}

            {activity.slice(0, 15).map((a) => (
              <div
                className="row row-sm"
                key={a.log_id}
              >
                <span>
                  {a.email} ┬и {a.action}
                </span>

                <small>
                  {new Date(a.created_at).toLocaleString()}
                </small>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div
    className="stat-card"
    data-testid={`stat-${label
      .toLowerCase()
      .replace(/ /g, "-")}`}
  >
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

function Products() {
  const [all, setAll] = useState([]);
  const [cats, setCats] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [models, setModels] = useState([]);

  const refresh = () =>
    apiFetch("/products?product_status=")
      .then(setAll)
      .catch(() => {});

  const refreshCatalog = async () => {
    try {
      const [categoryData, subcategoryData] =
        await Promise.all([
          apiFetch("/categories"),
          apiFetch("/subcategories"),
        ]);

      setCats(categoryData || []);
      setSubcats(subcategoryData || []);
    } catch (err) {
      toast.error(
        err.message || "Unable to load catalog structure"
      );
    }
  };

  useEffect(() => {
    refresh();
    refreshCatalog();

    apiFetch("/ai/models")
      .then((items) => {
        setModels(items);

        setEditing((current) =>
          current
            ? {
                ...current,
                _ai_model:
                  current._ai_model ||
                  items[0]?.id ||
                  "",
              }
            : current
        );
      })
      .catch(() => {});
  }, []);

  const getSubcategories = (categoryId) =>
    subcats
      .filter(
        (sub) =>
          sub.category_id === categoryId &&
          sub.hidden !== true
      )
      .sort(
        (a, b) =>
          Number(a.sort_order || 0) -
            Number(b.sort_order || 0) ||
          String(a.name || "").localeCompare(
            String(b.name || "")
          )
      );

  const findCategoryForProduct = (product) => {
    if (!product) {
      return null;
    }

    return (
      cats.find(
        (category) =>
          category.id === product.category_id
      ) ||
      cats.find(
        (category) =>
          category.name === product.category
      ) ||
      cats[0] ||
      null
    );
  };

  const findSubcategoryForProduct = (
    product,
    categoryId
  ) => {
    if (!product || !categoryId) {
      return null;
    }

    const matchingSubcategories =
      getSubcategories(categoryId);

    return (
      matchingSubcategories.find(
        (subcategory) =>
          subcategory.id ===
          product.subcategory_id
      ) ||
      matchingSubcategories.find(
        (subcategory) =>
          subcategory.name ===
          product.subcategory_name
      ) ||
      null
    );
  };

  const prepareProductForEditing = (product) => {
    const category =
      findCategoryForProduct(product);

    const subcategory =
      findSubcategoryForProduct(
        product,
        category?.id
      );

    return {
      ...product,
      category_id:
        category?.id ||
        product?.category_id ||
        "",
      category:
        category?.name ||
        product?.category ||
        "",
      category_name:
        category?.name ||
        product?.category_name ||
        product?.category ||
        "",
      subcategory_id:
        subcategory?.id ||
        product?.subcategory_id ||
        "",
      subcategory_name:
        subcategory?.name ||
        product?.subcategory_name ||
        "",
      colors: Array.isArray(product?.colors)
        ? product.colors
        : product?.colors || [],
      tags: Array.isArray(product?.tags)
        ? product.tags
        : product?.tags || [],
      images: Array.isArray(product?.images)
        ? product.images
        : [],
      _ai_model:
        product?._ai_model ||
        models[0]?.id ||
        "",
    };
  };

  const visible = useMemo(
    () =>
      all.filter(
        (p) =>
          (!q ||
            p.name
              ?.toLowerCase()
              .includes(q.toLowerCase()) ||
            p.sku
              ?.toLowerCase()
              .includes(q.toLowerCase())) &&
          (!filterCat ||
            p.category_id === filterCat ||
            p.category === filterCat) &&
          (!filterStatus || p.status === filterStatus)
      ),
    [all, q, filterCat, filterStatus]
  );

  const emptyProduct = () => {
    const defaultCategory =
      cats.find(
        (category) =>
          category.name
            ?.trim()
            .toLowerCase() === "shawls"
      ) ||
      cats.find(
        (category) =>
          category.hidden !== true
      ) ||
      null;

    const defaultSubcategories =
      defaultCategory
        ? getSubcategories(
            defaultCategory.id
          )
        : [];

    const defaultSubcategory =
      defaultSubcategories[0] || null;

    return {
      name: "",
      category:
        defaultCategory?.name || "",
      category_id:
        defaultCategory?.id || "",
      category_name:
        defaultCategory?.name || "",
      subcategory_id:
        defaultSubcategory?.id || "",
      subcategory_name:
        defaultSubcategory?.name || "",
      price: 0,
      compare_at_price: null,
      short_description: "",
      description: "",
      material: "",
      dimensions: "",
      colors: [],
      stock: 0,
      sku: "",
      tags: [],
      featured: false,
      status: "published",
      seo_title: "",
      seo_description: "",
      images: [],
      slug: "",
    };
  };

  const handleCategoryChange = (categoryId) => {
    const category = cats.find(
      (item) => item.id === categoryId
    );

    const availableSubcategories =
      getSubcategories(categoryId);

    const firstSubcategory =
      availableSubcategories[0] || null;

    setEditing((current) => ({
      ...current,
      category_id: categoryId,
      category:
        category?.name || "",
      category_name:
        category?.name || "",
      subcategory_id:
        firstSubcategory?.id || "",
      subcategory_name:
        firstSubcategory?.name || "",
    }));
  };

  const handleSubcategoryChange = (
    subcategoryId
  ) => {
    const subcategory = subcats.find(
      (item) =>
        item.id === subcategoryId
    );

    setEditing((current) => ({
      ...current,
      subcategory_id:
        subcategory?.id || "",
      subcategory_name:
        subcategory?.name || "",
    }));
  };

  const save = async (e) => {
    e.preventDefault();

    const category = cats.find(
      (item) =>
        item.id === editing.category_id
    );

    if (!category) {
      toast.error(
        "Please select a valid category."
      );
      return;
    }

    const subcategory = subcats.find(
      (item) =>
        item.id === editing.subcategory_id &&
        item.category_id ===
          editing.category_id
    );

    if (!subcategory) {
      toast.error(
        "Please select a valid subcategory."
      );
      return;
    }

    const payload = {
      ...editing,

      category_id: category.id,
      category: category.name,
      category_name: category.name,

      subcategory_id: subcategory.id,
      subcategory_name:
        subcategory.name,

      price: Number(editing.price) || 0,

      stock: Number(editing.stock) || 0,

      compare_at_price:
        editing.compare_at_price
          ? Number(
              editing.compare_at_price
            )
          : null,

      colors:
        typeof editing.colors === "string"
          ? editing.colors
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : editing.colors,

      tags:
        typeof editing.tags === "string"
          ? editing.tags
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean)
          : editing.tags,
    };

    try {
      if (editing.id) {
        await apiFetch(
          `/products/${editing.id}`,
          {
            method: "PUT",
            body: JSON.stringify(payload),
          }
        );
      } else {
        await apiFetch("/products", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      toast.success("Product saved");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to save product"
      );
    }
  };

  const del = async (id, name) => {
    if (
      !window.confirm(
        `Delete "${name}"? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await apiFetch(`/products/${id}`, {
        method: "DELETE",
      });

      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const dup = async (id) => {
    try {
      await apiFetch(
        `/products/${id}/duplicate`,
        {
          method: "POST",
        }
      );

      toast.success("Duplicated");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const bulk = async (
    action,
    extra = {}
  ) => {
    if (!selected.length) {
      return toast.error(
        "No products selected"
      );
    }

    if (
      action === "delete" &&
      !window.confirm(
        `Delete ${selected.length} products?`
      )
    ) {
      return;
    }

    try {
      await apiFetch("/products/bulk", {
        method: "POST",
        body: JSON.stringify({
          ids: selected,
          action,
          ...extra,
        }),
      });

      toast.success(
        `Bulk ${action} done`
      );

      setSelected([]);
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const aiGenerate = async () => {
    if (!editing?.name) {
      return toast.error(
        "Enter a product name first"
      );
    }

    setAiBusy(true);

    try {
      const data = await apiFetch(
        "/ai/generate-description",
        {
          method: "POST",
          body: JSON.stringify({
            product_name:
              editing.name,

            category:
              editing.category,

            material:
              editing.material,

            model:
              editing._ai_model ||
              models[0]?.id ||
              "",
          }),
        }
      );

      setEditing((e) => ({
        ...e,

        short_description:
          data.short_description ||
          e.short_description,

        description:
          data.description ||
          e.description,

        tags:
          data.tags &&
          data.tags.length
            ? data.tags
            : e.tags,

        seo_title:
          data.seo_title ||
          e.seo_title,

        seo_description:
          data.seo_description ||
          e.seo_description,
      }));

      toast.success(
        "AI copy generated"
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Catalog
          </span>

          <h1>
            Products{" "}
            <small>
              {visible.length}
            </small>
          </h1>
        </div>

        <button
          className="button button-dark small"
          onClick={() =>
            setEditing(
              emptyProduct()
            )
          }
          data-testid="add-product-button"
        >
          <Plus size={14} />
          Add product
        </button>
      </header>

      <div className="admin-toolbar">
        <input
          placeholder="Search by name or SKU"
          value={q}
          onChange={(e) =>
            setQ(e.target.value)
          }
          data-testid="product-search"
        />

        <select
          value={filterCat}
          onChange={(e) =>
            setFilterCat(
              e.target.value
            )
          }
          data-testid="product-filter-category"
        >
          <option value="">
            All categories
          </option>

          {cats
            .filter(
              (c) =>
                c.hidden !== true
            )
            .map((c) => (
              <option
                key={c.id}
                value={c.id}
              >
                {c.name}
              </option>
            ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) =>
            setFilterStatus(
              e.target.value
            )
          }
          data-testid="product-filter-status"
        >
          <option value="">
            All statuses
          </option>

          <option value="published">
            Published
          </option>

          <option value="draft">
            Draft
          </option>
        </select>

        {selected.length > 0 && (
          <div className="bulk-actions">
            <span>
              {selected.length} selected
            </span>

            <button
              onClick={() =>
                bulk("publish")
              }
              data-testid="bulk-publish"
            >
              Publish
            </button>

            <button
              onClick={() =>
                bulk("draft")
              }
              data-testid="bulk-draft"
            >
              Draft
            </button>

            <button
              onClick={() =>
                bulk("delete")
              }
              className="danger"
              data-testid="bulk-delete"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>Product</th>
              <th>Category</th>
              <th>Subcategory</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {visible.map((p) => (
              <tr
                key={p.id}
                data-testid={`product-row-${p.id}`}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selected.includes(
                      p.id
                    )}
                    onChange={(e) =>
                      setSelected((s) =>
                        e.target.checked
                          ? [
                              ...s,
                              p.id,
                            ]
                          : s.filter(
                              (x) =>
                                x !==
                                p.id
                            )
                      )
                    }
                    data-testid={`select-${p.id}`}
                  />
                </td>

                <td className="prod-cell">
                  <img
                    src={mainImage(p)}
                    alt=""
                  />

                  <div>
                    <b>{p.name}</b>
                    <small>
                      {p.sku || "—"}
                    </small>
                  </div>
                </td>

                <td>
                  {p.category_name ||
                    p.category ||
                    "—"}
                </td>

                <td>
                  {p.subcategory_name ||
                    "—"}
                </td>

                <td>
                  {money(p.price)}
                </td>

                <td
                  className={
                    p.stock === 0
                      ? "danger"
                      : p.stock <= 3
                      ? "warn"
                      : ""
                  }
                >
                  {p.stock}
                </td>

                <td>
                  <span
                    className={`pill pill-${p.status}`}
                  >
                    {p.status}
                  </span>
                </td>

                <td className="actions">
                  <button
                    onClick={() =>
                      setEditing(
                        prepareProductForEditing(
                          p
                        )
                      )
                    }
                    aria-label="Edit"
                    data-testid={`edit-product-${p.id}`}
                  >
                    <Pencil size={14} />
                  </button>

                  <button
                    onClick={() =>
                      dup(p.id)
                    }
                    aria-label="Duplicate"
                    data-testid={`dup-product-${p.id}`}
                  >
                    <Copy size={14} />
                  </button>

                  <button
                    onClick={() =>
                      del(
                        p.id,
                        p.name
                      )
                    }
                    aria-label="Delete"
                    data-testid={`delete-product-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal
        >
          <form
            className="admin-modal-inner"
            onSubmit={save}
          >
            <header>
              <h2>
                {editing.id
                  ? "Edit"
                  : "Add"}{" "}
                product
              </h2>

              <button
                type="button"
                onClick={() =>
                  setEditing(null)
                }
                data-testid="close-product-editor"
              >
                <X size={18} />
              </button>
            </header>

            <div className="form-grid">
              <label className="full">
                Images

                <ImageUploader
                  images={
                    editing.images ||
                    []
                  }
                  onChange={(
                    images
                  ) =>
                    setEditing({
                      ...editing,
                      images,
                    })
                  }
                />
              </label>

              <label>
                Name

                <input
                  required
                  value={
                    editing.name ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      name:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-name"
                />
              </label>

              <label>
                Category

                <select
                  required
                  value={
                    editing.category_id ||
                    ""
                  }
                  onChange={(e) =>
                    handleCategoryChange(
                      e.target.value
                    )
                  }
                  data-testid="product-field-category"
                >
                  <option value="">
                    Select category
                  </option>

                  {cats
                    .filter(
                      (category) =>
                        category.hidden !==
                        true
                    )
                    .map(
                      (
                        category
                      ) => (
                        <option
                          key={
                            category.id
                          }
                          value={
                            category.id
                          }
                        >
                          {
                            category.name
                          }
                        </option>
                      )
                    )}
                </select>
              </label>

              <label>
                Subcategory

                <select
                  required
                  value={
                    editing.subcategory_id ||
                    ""
                  }
                  onChange={(e) =>
                    handleSubcategoryChange(
                      e.target.value
                    )
                  }
                  disabled={
                    !editing.category_id ||
                    !getSubcategories(
                      editing.category_id
                    ).length
                  }
                  data-testid="product-field-subcategory"
                >
                  <option value="">
                    {editing.category_id
                      ? getSubcategories(
                          editing.category_id
                        ).length
                        ? "Select subcategory"
                        : "No subcategories"
                      : "Select category first"}
                  </option>

                  {getSubcategories(
                    editing.category_id
                  ).map(
                    (
                      subcategory
                    ) => (
                      <option
                        key={
                          subcategory.id
                        }
                        value={
                          subcategory.id
                        }
                      >
                        {
                          subcategory.name
                        }
                      </option>
                    )
                  )}
                </select>

                <small className="muted">
                  Products must belong
                  to a subcategory.
                </small>
              </label>

              <label>
                Price

                <input
                  type="number"
                  step="0.01"
                  required
                  value={
                    editing.price
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      price:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-price"
                />
              </label>

              <label>
                Compare-at price

                <input
                  type="number"
                  step="0.01"
                  value={
                    editing.compare_at_price ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      compare_at_price:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label>
                SKU

                <input
                  value={
                    editing.sku ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      sku:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-sku"
                />
              </label>

              <label>
                Material

                <input
                  value={
                    editing.material ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      material:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-material"
                />
              </label>

              <label>
                Dimensions

                <input
                  value={
                    editing.dimensions ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      dimensions:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label>
                Stock

                <input
                  type="number"
                  value={
                    editing.stock
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      stock:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-stock"
                />
              </label>

              <label>
                Colours
                (comma-separated)

                <input
                  value={
                    Array.isArray(
                      editing.colors
                    )
                      ? editing.colors.join(
                          ", "
                        )
                      : editing.colors ||
                        ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      colors:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label>
                Tags
                (comma-separated)

                <input
                  value={
                    Array.isArray(
                      editing.tags
                    )
                      ? editing.tags.join(
                          ", "
                        )
                      : editing.tags ||
                        ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      tags:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label>
                Status

                <select
                  value={
                    editing.status
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      status:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-status"
                >
                  <option value="published">
                    Published
                  </option>

                  <option value="draft">
                    Draft
                  </option>
                </select>
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    !!editing.featured
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      featured:
                        e.target
                          .checked,
                    })
                  }
                  data-testid="product-field-featured"
                />

                Featured on homepage
              </label>

              <label className="full">
                Short description

                <input
                  value={
                    editing.short_description ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      short_description:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-short-desc"
                />
              </label>

              <label className="full ai-field">
                <span className="ai-label">
                  Full description

                  <span className="ai-controls">
                    <select
                      value={
                        editing._ai_model ||
                        "anthropic/claude-sonnet-4-5-20250929"
                      }
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          _ai_model:
                            e.target
                              .value,
                        })
                      }
                      aria-label="AI model"
                      data-testid="ai-model-select"
                    >
                      {models.map(
                        (m) => (
                          <option
                            key={m.id}
                            value={
                              m.id
                            }
                          >
                            {m.label}
                          </option>
                        )
                      )}
                    </select>

                    <button
                      type="button"
                      onClick={
                        aiGenerate
                      }
                      disabled={
                        aiBusy
                      }
                      className="ai-btn"
                      data-testid="ai-generate-button"
                    >
                      <Sparkles
                        size={
                          13
                        }
                      />{" "}
                      {aiBusy
                        ? "Writing…"
                        : "AI write"}
                    </button>
                  </span>
                </span>

                <textarea
                  rows={6}
                  value={
                    editing.description ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      description:
                        e.target
                          .value,
                    })
                  }
                  data-testid="product-field-description"
                />
              </label>

              <label className="full">
                SEO title

                <input
                  value={
                    editing.seo_title ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      seo_title:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label className="full">
                SEO description

                <input
                  value={
                    editing.seo_description ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      seo_description:
                        e.target
                          .value,
                    })
                  }
                />
              </label>

              <label className="full">
                URL slug
                (optional)

                <input
                  value={
                    editing.slug ||
                    ""
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      slug:
                        e.target
                          .value,
                    })
                  }
                  placeholder="auto-generated from name"
                />
              </label>
            </div>

            <footer>
              <button
                className="button button-dark"
                type="submit"
                data-testid="save-product-button"
              >
                Save product{" "}
                <ArrowRight size={16} />
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [subcats, setSubcats] = useState([]);

  const [editingCategory, setEditingCategory] =
    useState(null);

  const [editingSubcategory, setEditingSubcategory] =
    useState(null);

  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);

    try {
      const [categoryData, subcategoryData] =
  await Promise.all([
    apiFetch("/categories?include_hidden=true"),
    apiFetch("/subcategories?include_hidden=true"),
  ]);

const activeCategories = (categoryData || []).filter(
  (category) => category.hidden !== true
);

const activeCategoryIds = new Set(
  activeCategories.map((category) => category.id)
);

const activeSubcategories = (subcategoryData || []).filter(
  (subcategory) =>
    subcategory.hidden !== true &&
    activeCategoryIds.has(subcategory.category_id)
);

setCats(activeCategories);
setSubcats(activeSubcategories);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to load catalog categories"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  /* =========================================================
     CATEGORY
     ========================================================= */

  const saveCategory = async (e) => {
    e.preventDefault();

    try {
      if (editingCategory.id) {
        await apiFetch(
          `/categories/${editingCategory.id}`,
          {
            method: "PUT",
            body: JSON.stringify(
              editingCategory
            ),
          }
        );
      } else {
        await apiFetch("/categories", {
          method: "POST",
          body: JSON.stringify(
            editingCategory
          ),
        });
      }

      toast.success("Category saved");
      setEditingCategory(null);
      await refresh();
    } catch (err) {
      toast.error(
        err.message || "Unable to save category"
      );
    }
  };

  const deleteCategory = async (category) => {
    const categorySubcategories =
      subcats.filter(
        (sub) =>
          sub.category_id === category.id
      );

    const products = await apiFetch(
      `/products?category=${encodeURIComponent(
        category.name
      )}&product_status=`
    ).catch(() => []);

    if (categorySubcategories.length) {
      toast.error(
        "This category still has subcategories. Delete or move them first."
      );
      return;
    }

    let moveTo = "";

    if (products.length) {
      const options = cats
        .filter(
          (item) =>
            item.id !== category.id &&
            item.hidden !== true
        )
        .map((item) => item.name);

      if (!options.length) {
        toast.error(
          "This category has products and there is no other category to move them to."
        );
        return;
      }

      moveTo = window.prompt(
        `${products.length} products use this category.\n\nMove them to one of:\n${options.join(
          " | "
        )}\n\nEnter the category name, or cancel to abort.`
      );

      if (!moveTo) {
        return;
      }

      const normalizedMoveTo =
        moveTo.trim().toLowerCase();

      const targetCategory = cats.find(
        (item) =>
          item.id !== category.id &&
          item.hidden !== true &&
          item.name
            ?.trim()
            .toLowerCase() ===
            normalizedMoveTo
      );

      if (!targetCategory) {
        toast.error(
          "That destination category was not found. Nothing was changed."
        );
        return;
      }

      moveTo = targetCategory.name;
    }

    if (
      !window.confirm(
        `Delete the category "${category.name}"?`
      )
    ) {
      return;
    }

    try {
      await apiFetch(
        `/categories/${category.id}${
          moveTo
            ? `?move_to=${encodeURIComponent(
                moveTo
              )}`
            : ""
        }`,
        {
          method: "DELETE",
        }
      );

      toast.success("Category removed");
      await refresh();
    } catch (err) {
      toast.error(
        err.message || "Unable to delete category"
      );
    }
  };

  /* =========================================================
     SUBCATEGORY
     ========================================================= */

  const saveSubcategory = async (e) => {
    e.preventDefault();

    if (!editingSubcategory.category_id) {
      toast.error("Select a parent category");
      return;
    }

    try {
      if (editingSubcategory.id) {
        await apiFetch(
          `/subcategories/${editingSubcategory.id}`,
          {
            method: "PUT",
            body: JSON.stringify(
              editingSubcategory
            ),
          }
        );
      } else {
        await apiFetch("/subcategories", {
          method: "POST",
          body: JSON.stringify(
            editingSubcategory
          ),
        });
      }

      toast.success("Subcategory saved");
      setEditingSubcategory(null);
      await refresh();
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to save subcategory"
      );
    }
  };

  const deleteSubcategory = async (
    subcategory
  ) => {
    if (
      !window.confirm(
        `Delete the subcategory "${subcategory.name}"?`
      )
    ) {
      return;
    }

    try {
      await apiFetch(
        `/subcategories/${subcategory.id}`,
        {
          method: "DELETE",
        }
      );

      toast.success("Subcategory removed");
      await refresh();
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to delete subcategory"
      );
    }
  };

  /* =========================================================
     IMAGE UPLOADS
     ========================================================= */

  const uploadCategoryImage = async (
    file,
    key
  ) => {
    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await fetch(
        `${API}/uploads/image`,
        {
          method: "POST",
          body: fd,
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || "Upload failed"
        );
      }

      setEditingCategory((current) => ({
        ...current,
        [key]: data.url,
      }));

      toast.success("Image uploaded");
    } catch (err) {
      toast.error(
        err.message || "Upload failed"
      );
    }
  };

  const uploadSubcategoryImage = async (
    file,
    key
  ) => {
    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await fetch(
        `${API}/uploads/image`,
        {
          method: "POST",
          body: fd,
          credentials: "include",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || "Upload failed"
        );
      }

      setEditingSubcategory((current) => ({
        ...current,
        [key]: data.url,
      }));

      toast.success("Image uploaded");
    } catch (err) {
      toast.error(
        err.message || "Upload failed"
      );
    }
  };

  /* =========================================================
     HELPERS
     ========================================================= */

  const getSubcategories = (categoryId) =>
    subcats
      .filter(
        (sub) =>
          sub.category_id === categoryId
      )
      .sort(
        (a, b) =>
          Number(a.sort_order || 0) -
            Number(b.sort_order || 0) ||
          String(a.name || "").localeCompare(
            String(b.name || "")
          )
      );

  const visibleCategories = [...cats].sort(
    (a, b) =>
      Number(a.sort_order || 0) -
        Number(b.sort_order || 0) ||
      String(a.name || "").localeCompare(
        String(b.name || "")
      )
  );

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Catalog structure
          </span>

          <h1>Categories</h1>

          <p className="muted">
            Organize your catalog into main
            categories and nested
            subcategories.
          </p>
        </div>

        <button
          className="button button-dark small"
          type="button"
          onClick={() =>
            setEditingCategory({
              name: "",
              slug: "",
              description: "",
              thumbnail: "",
              banner: "",
              sort_order:
                cats.length + 1,
              hidden: false,
            })
          }
          data-testid="add-category-button"
        >
          <Plus size={14} />
          Add category
        </button>
      </header>

      {loading ? (
        <div className="admin-card">
          <p className="muted">
            Loading catalog…
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 18,
          }}
        >
          {visibleCategories.map(
            (category) => {
              const categorySubcategories =
                getSubcategories(
                  category.id
                );

              return (
                <section
                  className="admin-card"
                  key={category.id}
                >
                  {/* =================================================
                     PARENT CATEGORY
                     ================================================= */}

                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "flex-start",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 16,
                        alignItems:
                          "flex-start",
                      }}
                    >
                      {category.thumbnail && (
                        <img
                          src={resolveImageUrl(
                            category.thumbnail
                          )}
                          alt={
                            category.name
                          }
                          style={{
                            width: 92,
                            height: 92,
                            objectFit:
                              "cover",
                            borderRadius: 8,
                          }}
                        />
                      )}

                      <div>
                        <span className="eyebrow">
                          Main category
                        </span>

                        <h2
                          style={{
                            margin:
                              "4px 0 6px",
                          }}
                        >
                          {category.name}{" "}
                          {category.hidden && (
                            <span className="pill-warn">
                              Hidden
                            </span>
                          )}
                        </h2>

                        <p
                          className="muted"
                          style={{
                            margin:
                              "0 0 5px",
                          }}
                        >
                          {category.description ||
                            "No description added."}
                        </p>

                        <small className="muted">
                          /{category.slug}
                        </small>
                      </div>
                    </div>

                    <div
                      className="actions"
                      style={{
                        display: "flex",
                        gap: 8,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setEditingCategory(
                            {
                              ...category,
                            }
                          )
                        }
                        aria-label={`Edit ${category.name}`}
                        data-testid={`edit-category-${category.slug}`}
                      >
                        <Pencil size={14} />
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          deleteCategory(
                            category
                          )
                        }
                        aria-label={`Delete ${category.name}`}
                        data-testid={`delete-category-${category.slug}`}
                      >
                        <Trash2
                          size={14}
                        />
                      </button>
                    </div>
                  </div>

                  {/* =================================================
                     SUBCATEGORIES
                     ================================================= */}

                  <div
                    style={{
                      marginTop: 22,
                      paddingTop: 18,
                      borderTop:
                        "1px solid rgba(0,0,0,.08)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap: 12,
                        flexWrap:
                          "wrap",
                      }}
                    >
                      <div>
                        <span className="eyebrow">
                          Collection groups
                        </span>

                        <h3
                          style={{
                            margin:
                              "4px 0 0",
                          }}
                        >
                          Subcategories
                        </h3>
                      </div>

                      <button
                        type="button"
                        className="button button-dark small"
                        onClick={() =>
                          setEditingSubcategory(
                            {
                              category_id:
                                category.id,
                              name: "",
                              slug: "",
                              description:
                                "",
                              thumbnail:
                                "",
                              banner:
                                "",
                              sort_order:
                                categorySubcategories.length +
                                1,
                              hidden:
                                false,
                            }
                          )
                        }
                      >
                        <Plus size={14} />
                        Add subcategory
                      </button>
                    </div>

                    {!categorySubcategories.length ? (
                      <div
                        style={{
                          padding:
                            "18px 0 4px",
                        }}
                      >
                        <p className="muted">
                          No subcategories
                          yet.
                        </p>
                      </div>
                    ) : (
                      <div
                        style={{
                          display:
                            "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(240px, 1fr))",
                          gap: 12,
                          marginTop: 14,
                        }}
                      >
                        {categorySubcategories.map(
                          (
                            subcategory
                          ) => (
                            <div
                              key={
                                subcategory.id
                              }
                              style={{
                                border:
                                  "1px solid rgba(0,0,0,.10)",
                                borderRadius:
                                  10,
                                padding:
                                  12,
                                display:
                                  "flex",
                                gap: 12,
                                alignItems:
                                  "flex-start",
                              }}
                            >
                              {subcategory.thumbnail ? (
                                <img
                                  src={resolveImageUrl(
                                    subcategory.thumbnail
                                  )}
                                  alt={
                                    subcategory.name
                                  }
                                  style={{
                                    width: 64,
                                    height: 64,
                                    objectFit:
                                      "cover",
                                    borderRadius:
                                      7,
                                    flexShrink:
                                      0,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 64,
                                    height: 64,
                                    borderRadius:
                                      7,
                                    background:
                                      "rgba(0,0,0,.04)",
                                    flexShrink:
                                      0,
                                  }}
                                />
                              )}

                              <div
                                style={{
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <div
                                  style={{
                                    display:
                                      "flex",
                                    justifyContent:
                                      "space-between",
                                    gap: 8,
                                  }}
                                >
                                  <strong>
                                    {
                                      subcategory.name
                                    }
                                  </strong>

                                  {subcategory.hidden && (
                                    <span className="pill-warn">
                                      Hidden
                                    </span>
                                  )}
                                </div>

                                <small className="muted">
                                  /
                                  {
                                    subcategory.slug
                                  }
                                </small>

                                <p
                                  className="muted"
                                  style={{
                                    margin:
                                      "6px 0",
                                    fontSize:
                                      13,
                                  }}
                                >
                                  {subcategory.description ||
                                    "No description added."}
                                </p>

                                <div
                                  className="actions"
                                  style={{
                                    display:
                                      "flex",
                                    gap: 6,
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditingSubcategory(
                                        {
                                          ...subcategory,
                                        }
                                      )
                                    }
                                    aria-label={`Edit ${subcategory.name}`}
                                  >
                                    <Pencil
                                      size={
                                        14
                                      }
                                    />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      deleteSubcategory(
                                        subcategory
                                      )
                                    }
                                    aria-label={`Delete ${subcategory.name}`}
                                  >
                                    <Trash2
                                      size={
                                        14
                                      }
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </section>
              );
            }
          )}

          {!visibleCategories.length && (
            <div className="admin-card">
              <p className="muted">
                No categories found.
              </p>
            </div>
          )}
        </div>
      )}

      {/* =========================================================
         CATEGORY MODAL
         ========================================================= */}

      {editingCategory && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal="true"
        >
          <form
            className="admin-modal-inner"
            onSubmit={saveCategory}
          >
            <header>
              <h2>
                {editingCategory.id
                  ? "Edit category"
                  : "Add category"}
              </h2>

              <button
                type="button"
                onClick={() =>
                  setEditingCategory(
                    null
                  )
                }
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="form-grid">
              <label>
                Name
                <input
                  required
                  value={
                    editingCategory.name ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingCategory(
                      (current) => ({
                        ...current,
                        name: e.target.value,
                      })
                    )
                  }
                  data-testid="cat-field-name"
                />
              </label>

              <label>
                URL slug
                <input
                  value={
                    editingCategory.slug ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingCategory(
                      (current) => ({
                        ...current,
                        slug: e.target.value,
                      })
                    )
                  }
                  placeholder="auto from name"
                  data-testid="cat-field-slug"
                />
              </label>

              <label className="full">
                Description
                <textarea
                  value={
                    editingCategory.description ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingCategory(
                      (current) => ({
                        ...current,
                        description:
                          e.target.value,
                      })
                    )
                  }
                />
              </label>

              <label className="full">
                Thumbnail
                <small className="muted">
                  Used for category cards
                  and visual catalog
                  navigation.
                </small>

                {editingCategory.thumbnail && (
                  <img
                    src={resolveImageUrl(
                      editingCategory.thumbnail
                    )}
                    alt=""
                    className="setting-preview"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0];

                    if (file) {
                      uploadCategoryImage(
                        file,
                        "thumbnail"
                      );
                    }
                  }}
                  data-testid="cat-thumbnail-upload"
                />
              </label>

              <label className="full">
                Banner
                <small className="muted">
                  Used for the category
                  page header.
                </small>

                {editingCategory.banner && (
                  <img
                    src={resolveImageUrl(
                      editingCategory.banner
                    )}
                    alt=""
                    className="setting-preview"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0];

                    if (file) {
                      uploadCategoryImage(
                        file,
                        "banner"
                      );
                    }
                  }}
                />
              </label>

              <label>
                Sort order
                <input
                  type="number"
                  value={
                    editingCategory.sort_order ??
                    0
                  }
                  onChange={(e) =>
                    setEditingCategory(
                      (current) => ({
                        ...current,
                        sort_order:
                          Number(
                            e.target.value
                          ),
                      })
                    )
                  }
                />
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    !!editingCategory.hidden
                  }
                  onChange={(e) =>
                    setEditingCategory(
                      (current) => ({
                        ...current,
                        hidden:
                          e.target.checked,
                      })
                    )
                  }
                />
                Hidden from site
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="button"
                onClick={() =>
                  setEditingCategory(
                    null
                  )
                }
              >
                Cancel
              </button>

              <button
                className="button button-dark"
                type="submit"
                data-testid="save-category-button"
              >
                Save category{" "}
                <ArrowRight size={16} />
              </button>
            </footer>
          </form>
        </div>
      )}

      {/* =========================================================
         SUBCATEGORY MODAL
         ========================================================= */}

      {editingSubcategory && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal="true"
        >
          <form
            className="admin-modal-inner"
            onSubmit={saveSubcategory}
          >
            <header>
              <h2>
                {editingSubcategory.id
                  ? "Edit subcategory"
                  : "Add subcategory"}
              </h2>

              <button
                type="button"
                onClick={() =>
                  setEditingSubcategory(
                    null
                  )
                }
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="form-grid">
              <label className="full">
                Parent category
                <select
                  required
                  value={
                    editingSubcategory.category_id ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        category_id:
                          e.target.value,
                      })
                    )
                  }
                >
                  <option value="">
                    Select parent category
                  </option>

                  {cats
                    .filter(
                      (category) =>
                        category.hidden !==
                        true
                    )
                    .map((category) => (
                      <option
                        key={
                          category.id
                        }
                        value={
                          category.id
                        }
                      >
                        {category.name}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Name
                <input
                  required
                  value={
                    editingSubcategory.name ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        name: e.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                URL slug
                <input
                  value={
                    editingSubcategory.slug ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        slug: e.target.value,
                      })
                    )
                  }
                  placeholder="auto from name"
                />
              </label>

              <label className="full">
                Description
                <textarea
                  value={
                    editingSubcategory.description ||
                    ""
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        description:
                          e.target.value,
                      })
                    )
                  }
                />
              </label>

              <label className="full">
                Thumbnail
                <small className="muted">
                  Used for subcategory
                  cards and navigation.
                </small>

                {editingSubcategory.thumbnail && (
                  <img
                    src={resolveImageUrl(
                      editingSubcategory.thumbnail
                    )}
                    alt=""
                    className="setting-preview"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0];

                    if (file) {
                      uploadSubcategoryImage(
                        file,
                        "thumbnail"
                      );
                    }
                  }}
                />
              </label>

              <label className="full">
                Banner
                <small className="muted">
                  Used for the
                  subcategory page
                  header.
                </small>

                {editingSubcategory.banner && (
                  <img
                    src={resolveImageUrl(
                      editingSubcategory.banner
                    )}
                    alt=""
                    className="setting-preview"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0];

                    if (file) {
                      uploadSubcategoryImage(
                        file,
                        "banner"
                      );
                    }
                  }}
                />
              </label>

              <label>
                Sort order
                <input
                  type="number"
                  value={
                    editingSubcategory.sort_order ??
                    0
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        sort_order:
                          Number(
                            e.target.value
                          ),
                      })
                    )
                  }
                />
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    !!editingSubcategory.hidden
                  }
                  onChange={(e) =>
                    setEditingSubcategory(
                      (current) => ({
                        ...current,
                        hidden:
                          e.target.checked,
                      })
                    )
                  }
                />
                Hidden from site
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="button"
                onClick={() =>
                  setEditingSubcategory(
                    null
                  )
                }
              >
                Cancel
              </button>

              <button
                className="button button-dark"
                type="submit"
              >
                Save subcategory{" "}
                <ArrowRight size={16} />
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const refresh = () => {
    const params = new URLSearchParams();

    if (status) params.set("order_status", status);
    if (q) params.set("q", q);

    apiFetch(`/orders?${params}`)
      .then(setOrders)
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
  }, [status, q]);

  const update = async (orderId, body) => {
    try {
      await apiFetch(
        `/orders/${orderId}/status`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        }
      );

      toast.success("Status updated");
      refresh();

      if (sel) setSel(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Orders</h1>
        </div>

        <a
          className="button button-dark small"
          href={`${API}/orders/export.csv`}
          target="_blank"
          rel="noreferrer"
          data-testid="export-orders"
        >
          <Download size={14} /> Export CSV
        </a>
      </header>

      <div className="admin-toolbar">
        <input
          placeholder="Search order#, name, email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="order-search"
        />

        <select
          value={status}
          onChange={(e) =>
            setStatus(e.target.value)
          }
          data-testid="order-filter-status"
        >
          <option value="">All statuses</option>

          {[
            "Order Placed",
            "Confirmed",
            "Processing",
            "Shipped",
            "Delivered",
            "Cancelled",
          ].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {orders.map((o) => (
              <tr
                key={o.order_id}
                data-testid={`order-row-${o.order_number}`}
              >
                <td>
                  <b>{o.order_number}</b>
                </td>

                <td>
                  {new Date(
                    o.created_at
                  ).toLocaleDateString()}
                </td>

                <td>
                  {o.customer?.name}
                  <br />
                  <small>
                    {o.customer?.email}
                  </small>
                </td>

                <td>
                  {o.items?.length} items
                </td>

                <td>{money(o.total)}</td>

                <td>
                  <span className="pill">
                    {o.status}
                  </span>
                </td>

                <td>
                  <button
                    onClick={() => setSel(o)}
                    data-testid={`view-order-${o.order_number}`}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sel && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal
        >
          <div className="admin-modal-inner">
            <header>
              <h2>Order {sel.order_number}</h2>

              <button
                type="button"
                onClick={() => setSel(null)}
              >
                <X size={18} />
              </button>
            </header>

            <div className="order-view">
              <div>
                <h4>Customer</h4>
                <p>
                  {sel.customer?.name}
                  <br />
                  {sel.customer?.email}
                  <br />
                  {sel.customer?.phone}
                </p>
              </div>

              <div>
                <h4>Delivery</h4>
                <p>
                  {sel.customer?.address || "—"}
                </p>
              </div>

              <div>
                <h4>Items</h4>

                {sel.items?.map((i, idx) => (
                  <div
                    key={idx}
                    className="row"
                  >
                    <span>
                      {i.name} ├Ќ {i.quantity}
                    </span>

                    <b>
                      {money(
                        i.price * i.quantity
                      )}
                    </b>
                  </div>
                ))}
              </div>

              <div className="row">
                <span>Total</span>
                <strong>
                  {money(sel.total)}
                </strong>
              </div>

              <div>
                <h4>Update status</h4>

                <StatusUpdater
                  order={sel}
                  onUpdate={update}
                />
              </div>

              <div>
                <h4>History</h4>

                {(sel.status_history || []).map(
                  (h, i) => (
                    <div
                      key={i}
                      className="row row-sm"
                    >
                      <span>{h.status}</span>
                      <small>
                        {new Date(
                          h.at
                        ).toLocaleString()}
                      </small>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusUpdater({ order, onUpdate }) {
  const [status, setStatus] = useState(
    order.status
  );
  const [courier, setCourier] = useState(
    order.courier || ""
  );
  const [tracking, setTracking] = useState(
    order.tracking || ""
  );
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);

  return (
    <div className="status-updater">
      <select
        value={status}
        onChange={(e) =>
          setStatus(e.target.value)
        }
        data-testid="status-select"
      >
        {[
          "Order Placed",
          "Confirmed",
          "Processing",
          "Shipped",
          "Delivered",
          "Cancelled",
        ].map((s) => (
          <option key={s}>{s}</option>
        ))}
      </select>

      <input
        placeholder="Courier"
        value={courier}
        onChange={(e) =>
          setCourier(e.target.value)
        }
        data-testid="status-courier"
      />

      <input
        placeholder="Tracking #"
        value={tracking}
        onChange={(e) =>
          setTracking(e.target.value)
        }
        data-testid="status-tracking"
      />

      <textarea
        placeholder="Internal note (optional)"
        value={note}
        onChange={(e) =>
          setNote(e.target.value)
        }
      />

      <label>
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) =>
            setNotify(e.target.checked)
          }
        />{" "}
        Email the customer
      </label>

      <button
        className="button button-dark"
        onClick={() =>
          onUpdate(order.order_id, {
            status,
            courier,
            tracking,
            internal_note: note,
            notify_customer: notify,
          })
        }
        data-testid="update-status-button"
      >
        Update
      </button>
    </div>
  );
}

function Customers() {
  const [list, setList] = useState([]);

  const refresh = () =>
    apiFetch("/customers")
      .then(setList)
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const toggle = async (u) => {
    try {
      await apiFetch(
        `/customers/${u.user_id}/toggle`,
        {
          method: "PUT",
        }
      );

      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">People</span>
          <h1>
            Customers <small>{list.length}</small>
          </h1>
        </div>
      </header>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Signed up</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {list.map((u) => (
              <tr
                key={u.user_id}
                data-testid={`customer-row-${u.email}`}
              >
                <td>{u.full_name || "—"}</td>

                <td>
                  {u.email}
                  {u.email_verified_at
                    ? " ✓"
                    : ""}
                </td>

                <td>
                  <span className="pill">
                    {u.role}
                  </span>
                </td>

                <td>
                  {u.created_at
                    ? new Date(
                        u.created_at
                      ).toLocaleDateString()
                    : "—"}
                </td>

                <td>
                  {u.role !== "owner" && (
                    <button
                      onClick={() => toggle(u)}
                      className="link"
                    >
                      {u.disabled
                        ? "Enable"
                        : "Disable"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reviews() {
  const [all, setAll] = useState([]);

  const refresh = () =>
    apiFetch("/admin/reviews")
      .then(setAll)
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const moderate = async (
    id,
    approved
  ) => {
    try {
      await apiFetch(
        `/admin/reviews/${id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            approved,
          }),
        }
      );

      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Social proof
          </span>
          <h1>Reviews</h1>
        </div>
      </header>

      {!all.length && (
        <p className="muted">
          No reviews yet.
        </p>
      )}

      {all.map((r) => (
        <div
          className="admin-card"
          key={r.review_id}
        >
          <div className="row">
            <span>
              <b>{r.author}</b> ┬и{" "}
              {"РўЁ".repeat(r.rating)}{" "}
              <small>{r.title}</small>
            </span>

            <span>
              {r.approved ? (
                <span className="pill pill-published">
                  Approved
                </span>
              ) : (
                <span className="pill pill-draft">
                  Pending
                </span>
              )}

              <button
                onClick={() =>
                  moderate(
                    r.review_id,
                    !r.approved
                  )
                }
                className="link"
              >
                {r.approved
                  ? "Hide"
                  : "Approve"}
              </button>
            </span>
          </div>

          <p>{r.body}</p>
        </div>
      ))}
    </div>
  );
}

function ContentPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] =
    useState(null);

  const refresh = () =>
    apiFetch("/content-pages")
      .then(setPages)
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const save = async (e) => {
    e.preventDefault();

    try {
      await apiFetch(
        `/content-pages/${editing.slug}`,
        {
          method: "PUT",
          body: JSON.stringify(editing),
        }
      );

      toast.success("Page updated");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Storefront content
          </span>
          <h1>Content pages</h1>
        </div>
      </header>

      <div className="admin-grid-cards">
        {pages.map((p) => (
          <div
            className="admin-card"
            key={p.slug}
            data-testid={`page-card-${p.slug}`}
          >
            <h3>{p.title}</h3>
            <small>/{p.slug}</small>

            <button
              className="button button-dark small"
              onClick={() => setEditing(p)}
              data-testid={`edit-page-${p.slug}`}
            >
              Edit
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal
        >
          <form
            className="admin-modal-inner"
            onSubmit={save}
          >
            <header>
              <h2>
                Edit ┬и {editing.slug}
              </h2>

              <button
                type="button"
                onClick={() => setEditing(null)}
              >
                <X size={18} />
              </button>
            </header>

            <div className="form-grid">
              <label className="full">
                Title
                <input
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      title: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Body (HTML allowed)
                <textarea
                  rows={14}
                  value={editing.body_html}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      body_html:
                        e.target.value,
                    })
                  }
                  data-testid="page-body-html"
                />
              </label>
            </div>

            <footer>
              <button
                className="button button-dark"
                data-testid="save-page-button"
              >
                Save <ArrowRight size={16} />
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function SiteSettings() {
  const [s, setS] = useState({});
  const [testimonial, setTestimonial] =
    useState({
      name: "",
      quote: "",
    });

  const refresh = () =>
    apiFetch("/site-settings")
      .then(setS)
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const upload = async (
    file,
    key
  ) => {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch(
      `${API}/uploads/image`,
      {
        method: "POST",
        body: fd,
        credentials: "include",
      }
    );

    const data = await res.json();

    if (!res.ok) {
      return toast.error(
        data.detail || "Upload failed"
      );
    }

    setS((cur) => ({
      ...cur,
      [key]: data.url,
    }));
  };

  const save = async () => {
    try {
      await apiFetch("/site-settings", {
        method: "PUT",
        body: JSON.stringify(s),
      });

      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const addTestimonial = () => {
    if (
      !testimonial.name ||
      !testimonial.quote
    ) {
      return;
    }

    setS((cur) => ({
      ...cur,
      testimonials: [
        ...(cur.testimonials || []),
        testimonial,
      ],
    }));

    setTestimonial({
      name: "",
      quote: "",
    });
  };

  const removeTestimonial = (i) =>
    setS((cur) => ({
      ...cur,
      testimonials: cur.testimonials.filter(
        (_, idx) => idx !== i
      ),
    }));

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Branding
          </span>
          <h1>Site settings</h1>
        </div>

        <button
          className="button button-dark small"
          onClick={save}
          data-testid="save-settings-button"
        >
          Save all
        </button>
      </header>

      <section className="admin-card">
        <h3>Brand</h3>

        <div className="form-grid">
          <label>
            Brand name
            <input
              value={s.brand_name || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  brand_name:
                    e.target.value,
                })
              }
              data-testid="setting-brand-name"
            />
          </label>

          <label>
            Logo

            {s.logo_url && (
              <img
                src={resolveImageUrl(
                  s.logo_url
                )}
                alt=""
                className="setting-preview small"
              />
            )}

            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                e.target.files[0] &&
                upload(
                  e.target.files[0],
                  "logo_url"
                )
              }
              data-testid="setting-logo-upload"
            />
          </label>
        </div>
      </section>

      <section className="admin-card">
        <h3>Homepage hero</h3>

        <div className="form-grid">
          <label className="full">
            Hero image

            {s.hero_image && (
              <img
                src={resolveImageUrl(
                  s.hero_image
                )}
                alt=""
                className="setting-preview"
              />
            )}

            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                e.target.files[0] &&
                upload(
                  e.target.files[0],
                  "hero_image"
                )
              }
              data-testid="setting-hero-upload"
            />
          </label>

          <label className="full">
            Headline
            <input
              value={
                s.hero_headline || ""
              }
              onChange={(e) =>
                setS({
                  ...s,
                  hero_headline:
                    e.target.value,
                })
              }
              data-testid="setting-headline"
            />
          </label>

          <label className="full">
            Subheadline
            <input
              value={
                s.hero_subheadline || ""
              }
              onChange={(e) =>
                setS({
                  ...s,
                  hero_subheadline:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Primary CTA
            <input
              value={
                s.hero_cta_primary || ""
              }
              onChange={(e) =>
                setS({
                  ...s,
                  hero_cta_primary:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Secondary CTA
            <input
              value={
                s.hero_cta_secondary || ""
              }
              onChange={(e) =>
                setS({
                  ...s,
                  hero_cta_secondary:
                    e.target.value,
                })
              }
            />
          </label>
        </div>
      </section>

      <section className="admin-card">
        <h3>Contact & footer</h3>

        <div className="form-grid">
          <label>
            Phone
            <input
              value={s.phone || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  phone: e.target.value,
                })
              }
              data-testid="setting-phone"
            />
          </label>

          <label>
            Email
            <input
              value={s.email || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  email: e.target.value,
                })
              }
              data-testid="setting-email"
            />
          </label>

          <label>
            WhatsApp
            <input
              value={s.whatsapp || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  whatsapp:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Address
            <input
              value={s.address || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  address:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Instagram URL
            <input
              value={s.instagram || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  instagram:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Facebook URL
            <input
              value={s.facebook || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  facebook:
                    e.target.value,
                })
              }
            />
          </label>

          <label>
            Pinterest URL
            <input
              value={s.pinterest || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  pinterest:
                    e.target.value,
                })
              }
            />
          </label>

          <label className="full">
            Map embed HTML
            <textarea
              rows={3}
              value={s.map_embed || ""}
              onChange={(e) =>
                setS({
                  ...s,
                  map_embed:
                    e.target.value,
                })
              }
            />
          </label>
        </div>
      </section>

      <section className="admin-card">
        <h3>Shipping</h3>

        <div className="form-grid">
          <label>
            Flat rate (₹)
            <input
              type="number"
              value={
                s.shipping_flat_rate || 0
              }
              onChange={(e) =>
                setS({
                  ...s,
                  shipping_flat_rate:
                    Number(
                      e.target.value
                    ),
                })
              }
            />
          </label>

          <label>
            Free shipping threshold (₹)
            <input
              type="number"
              value={
                s.free_shipping_threshold ||
                0
              }
              onChange={(e) =>
                setS({
                  ...s,
                  free_shipping_threshold:
                    Number(
                      e.target.value
                    ),
                })
              }
            />
          </label>
        </div>
      </section>

      <section className="admin-card">
        <h3>
          Testimonials (shown only once
          added by you)
        </h3>

        {(s.testimonials || []).map(
          (t, i) => (
            <div
              className="row"
              key={i}
            >
              <span>
                <b>{t.name}</b> Рђћ{" "}
                {t.quote}
              </span>

              <button
                className="link"
                onClick={() =>
                  removeTestimonial(i)
                }
              >
                Remove
              </button>
            </div>
          )
        )}

        <div className="form-grid">
          <label>
            Name
            <input
              value={testimonial.name}
              onChange={(e) =>
                setTestimonial({
                  ...testimonial,
                  name: e.target.value,
                })
              }
            />
          </label>

          <label className="full">
            Quote
            <input
              value={testimonial.quote}
              onChange={(e) =>
                setTestimonial({
                  ...testimonial,
                  quote:
                    e.target.value,
                })
              }
            />
          </label>
        </div>

        <button
          className="button button-dark small"
          onClick={addTestimonial}
          type="button"
        >
          Add testimonial
        </button>
      </section>
    </div>
  );
}

/* =========================================================
   SEO MANAGER
   ========================================================= */

const SEO_SECTIONS = [
  {
    type: "global",
    key: "global",
    label: "Global Website SEO",
    description:
      "Default SEO information for the website when a more specific page does not override it.",
  },
  {
    type: "page",
    key: "home",
    label: "Homepage",
    description:
      "SEO settings for the main GMK Handicrafts homepage.",
  },
  {
    type: "page",
    key: "pherans",
    label: "Pherans",
    description:
      "SEO settings for Kashmiri Pherans, Til Pherans and Aari Pherans.",
  },
  {
    type: "page",
    key: "shawls",
    label: "Shawls",
    description:
      "SEO settings for the main Kashmiri shawls collection.",
  },
  {
    type: "page",
    key: "pashmina",
    label: "Pashmina",
    description:
      "SEO settings for Kashmiri Pashmina shawls.",
  },
  {
    type: "page",
    key: "tosha",
    label: "Tosha",
    description:
      "SEO settings for Kashmiri Tosha shawls.",
  },
  {
    type: "page",
    key: "tusha",
    label: "Tusha",
    description:
      "SEO settings for Kashmiri Tusha shawls.",
  },
  {
    type: "page",
    key: "jamas",
    label: "Jamas",
    description:
      "SEO settings for Pashmina, Tosha and Tusha Jamas.",
  },
  {
    type: "page",
    key: "kaleen",
    label: "Kaleen",
    description:
      "SEO settings for traditional Kashmiri Kaleen and carpets.",
  },
];

const SEO_ROBOTS = [
  "index, follow",
  "noindex, follow",
  "index, nofollow",
  "noindex, nofollow",
];

const SEO_INTENTS = [
  "commercial",
  "informational",
  "navigational",
  "local",
];

function emptySEO() {
  return {
    scope_type: "page",
    scope_key: "home",
    title: "",
    description: "",
    focus_keyword: "",
    keywords: [],
    search_phrases: [],
    image_alt_default: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: "",
    robots: "index, follow",
  };
}

function SEOManager() {
  const [activeSection, setActiveSection] =
    useState(SEO_SECTIONS[1]);

  const [seoRecords, setSeoRecords] =
    useState({});

  const [editing, setEditing] =
    useState(emptySEO());

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [keywords, setKeywords] =
    useState([]);

  const [keywordLoading, setKeywordLoading] =
    useState(true);

  const [keywordEditing, setKeywordEditing] =
    useState(null);

  const [keywordSearch, setKeywordSearch] =
    useState("");

  const [keywordIntent, setKeywordIntent] =
    useState("");

  const loadSEO = async () => {
    setLoading(true);

    try {
      const records = await apiFetch(
        "/seo-settings"
      );

      const map = {};

      (records || []).forEach((record) => {
        map[
          `${record.scope_type}:${record.scope_key}`
        ] = record;
      });

      setSeoRecords(map);

      const current =
        map[
          `${activeSection.type}:${activeSection.key}`
        ];

      setEditing(
        current
          ? {
              ...emptySEO(),
              ...current,
            }
          : {
              ...emptySEO(),
              scope_type: activeSection.type,
              scope_key: activeSection.key,
            }
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to load SEO settings"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadKeywords = async () => {
    setKeywordLoading(true);

    try {
      const records = await apiFetch(
        "/seo-keywords"
      );

      setKeywords(records || []);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to load SEO keywords"
      );
    } finally {
      setKeywordLoading(false);
    }
  };

  useEffect(() => {
    loadSEO();
    loadKeywords();
  }, []);

  useEffect(() => {
    const current =
      seoRecords[
        `${activeSection.type}:${activeSection.key}`
      ];

    setEditing(
      current
        ? {
            ...emptySEO(),
            ...current,
          }
        : {
            ...emptySEO(),
            scope_type: activeSection.type,
            scope_key: activeSection.key,
          }
    );
  }, [activeSection, seoRecords]);

  const updateField = (field, value) => {
    setEditing((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const addListItem = (field, value) => {
    const clean = String(value || "")
      .trim()
      .replace(/\s+/g, " ");

    if (!clean) return;

    setEditing((current) => ({
      ...current,
      [field]: Array.from(
        new Set([
          ...(Array.isArray(current[field])
            ? current[field]
            : []),
          clean,
        ])
      ),
    }));
  };

  const removeListItem = (
    field,
    index
  ) => {
    setEditing((current) => ({
      ...current,
      [field]: (
        Array.isArray(current[field])
          ? current[field]
          : []
      ).filter((_, i) => i !== index),
    }));
  };

  const saveSEO = async () => {
    if (!editing.title?.trim()) {
      toast.error("SEO title is required");
      return;
    }

    if (!editing.description?.trim()) {
      toast.error(
        "Meta description is required"
      );
      return;
    }

    if (editing.title.length > 70) {
      toast.error(
        "SEO title should be 70 characters or fewer"
      );
      return;
    }

    if (editing.description.length > 320) {
      toast.error(
        "Meta description should be 320 characters or fewer"
      );
      return;
    }

    setSaving(true);

    try {
      const saved = await apiFetch(
        `/seo-settings/${editing.scope_type}/${editing.scope_key}`,
        {
          method: "PUT",
          body: JSON.stringify({
            title: editing.title.trim(),
            description:
              editing.description.trim(),
            focus_keyword:
              editing.focus_keyword?.trim() ||
              "",
            keywords:
              editing.keywords || [],
            search_phrases:
              editing.search_phrases || [],
            image_alt_default:
              editing.image_alt_default?.trim() ||
              "",
            og_title:
              editing.og_title?.trim() ||
              "",
            og_description:
              editing.og_description?.trim() ||
              "",
            og_image:
              editing.og_image?.trim() ||
              "",
            canonical_url:
              editing.canonical_url?.trim() ||
              "",
            robots:
              editing.robots ||
              "index, follow",
          }),
        }
      );

      const key = `${editing.scope_type}:${editing.scope_key}`;

      setSeoRecords((current) => ({
        ...current,
        [key]: saved,
      }));

      setEditing({
        ...emptySEO(),
        ...saved,
      });

      toast.success(
        `${activeSection.label} SEO saved`
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to save SEO settings"
      );
    } finally {
      setSaving(false);
    }
  };

  const resetSEO = async () => {
    if (
      !window.confirm(
        `Reset ${activeSection.label} SEO to the built-in defaults?`
      )
    ) {
      return;
    }

    try {
      await apiFetch(
        `/seo-settings/${activeSection.type}/${activeSection.key}`,
        {
          method: "DELETE",
        }
      );

      const key = `${activeSection.type}:${activeSection.key}`;

      setSeoRecords((current) => {
        const next = {
          ...current,
        };
        delete next[key];
        return next;
      });

      toast.success(
        `${activeSection.label} reset to defaults`
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to reset SEO settings"
      );
    }
  };

  const filteredKeywords = useMemo(() => {
    const query =
      keywordSearch.trim().toLowerCase();

    return keywords.filter((item) => {
      const matchesSearch =
        !query ||
        item.term
          ?.toLowerCase()
          .includes(query) ||
        item.target_scope
          ?.toLowerCase()
          .includes(query) ||
        item.notes
          ?.toLowerCase()
          .includes(query);

      const matchesIntent =
        !keywordIntent ||
        item.intent === keywordIntent;

      return (
        matchesSearch &&
        matchesIntent
      );
    });
  }, [
    keywords,
    keywordSearch,
    keywordIntent,
  ]);

  const saveKeyword = async (event) => {
    event.preventDefault();

    if (!keywordEditing?.term?.trim()) {
      toast.error("Enter a keyword");
      return;
    }

    try {
      const payload = {
        term: keywordEditing.term.trim(),
        intent:
          keywordEditing.intent ||
          "commercial",
        target_scope:
          keywordEditing.target_scope ||
          "global",
        priority: Math.max(
          0,
          Math.min(
            100,
            Number(
              keywordEditing.priority ?? 50
            )
          )
        ),
        active:
          keywordEditing.active !== false,
        notes:
          keywordEditing.notes?.trim() ||
          "",
      };

      let saved;

      if (keywordEditing.keyword_id) {
        saved = await apiFetch(
          `/seo-keywords/${keywordEditing.keyword_id}`,
          {
            method: "PUT",
            body: JSON.stringify(payload),
          }
        );
      } else {
        saved = await apiFetch(
          "/seo-keywords",
          {
            method: "POST",
            body: JSON.stringify(payload),
          }
        );
      }

      setKeywords((current) => {
        if (keywordEditing.keyword_id) {
          return current.map((item) =>
            item.keyword_id ===
            keywordEditing.keyword_id
              ? saved
              : item
          );
        }

        return [saved, ...current];
      });

      setKeywordEditing(null);

      toast.success(
        "SEO keyword saved"
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to save keyword"
      );
    }
  };

  const deleteKeyword = async (
    keyword
  ) => {
    if (
      !window.confirm(
        `Delete "${keyword.term}" from the SEO keyword library?`
      )
    ) {
      return;
    }

    try {
      await apiFetch(
        `/seo-keywords/${keyword.keyword_id}`,
        {
          method: "DELETE",
        }
      );

      setKeywords((current) =>
        current.filter(
          (item) =>
            item.keyword_id !==
            keyword.keyword_id
        )
      );

      toast.success("Keyword deleted");
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to delete keyword"
      );
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Search visibility
          </span>

          <h1>SEO Manager</h1>

          <p className="muted">
            Manage how GMK Handicrafts is
            presented to search engines and
            social platforms.
          </p>
        </div>
      </header>

      <div
        className="admin-card"
        style={{ marginBottom: 20 }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <Search size={22} />

          <div>
            <strong>
              Your SEO system is now
              centralized
            </strong>

            <p
              className="muted"
              style={{
                margin:
                  "6px 0 0",
              }}
            >
              Set page titles, descriptions,
              search intent, keywords,
              image text and indexing rules
              here. These settings will later
              control the public website
              metadata.
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(210px, 280px) minmax(0, 1fr)",
          gap: 20,
          alignItems: "start",
        }}
      >
        <div className="admin-card">
          <h3
            style={{
              marginTop: 0,
            }}
          >
            SEO pages
          </h3>

          <div
            style={{
              display: "grid",
              gap: 6,
            }}
          >
            {SEO_SECTIONS.map(
              (section) => {
                const isActive =
                  activeSection.type ===
                    section.type &&
                  activeSection.key ===
                    section.key;

                const hasSaved =
                  !!seoRecords[
                    `${section.type}:${section.key}`
                  ];

                return (
                  <button
                    key={`${section.type}:${section.key}`}
                    type="button"
                    onClick={() =>
                      setActiveSection(
                        section
                      )
                    }
                    style={{
                      textAlign: "left",
                      border: "1px solid",
                      borderColor:
                        isActive
                          ? "currentColor"
                          : "rgba(0,0,0,.10)",
                      background:
                        isActive
                          ? "rgba(0,0,0,.05)"
                          : "transparent",
                      padding:
                        "10px 12px",
                      borderRadius: 8,
                      cursor:
                        "pointer",
                    }}
                  >
                    <span
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        gap: 8,
                      }}
                    >
                      <span>
                        {section.label}
                      </span>

                      {hasSaved && (
                        <small>
                          Saved
                        </small>
                      )}
                    </span>
                  </button>
                );
              }
            )}
          </div>
        </div>

        <div>
          <SEOEditor
            editing={editing}
            loading={loading}
            saving={saving}
            activeSection={
              activeSection
            }
            updateField={
              updateField
            }
            addListItem={
              addListItem
            }
            removeListItem={
              removeListItem
            }
            saveSEO={saveSEO}
            resetSEO={resetSEO}
          />
        </div>
      </div>

      <div
        className="admin-card"
        style={{
          marginTop: 28,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            gap: 16,
            alignItems:
              "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <span className="eyebrow">
              Master database
            </span>

            <h2
              style={{
                margin:
                  "4px 0 6px",
              }}
            >
              SEO Keyword Library
            </h2>

            <p className="muted">
              Keep your important search
              terms organized by intent and
              target page. These are your
              strategic SEO terms — not
              random keyword stuffing.
            </p>
          </div>

          <button
            className="button button-dark small"
            type="button"
            onClick={() =>
              setKeywordEditing({
                term: "",
                intent:
                  "commercial",
                target_scope:
                  "global",
                priority: 50,
                active: true,
                notes: "",
              })
            }
          >
            <Plus size={14} /> Add keyword
          </button>
        </div>

        <div
          className="admin-toolbar"
          style={{
            marginTop: 18,
          }}
        >
          <input
            placeholder="Search keywords..."
            value={keywordSearch}
            onChange={(e) =>
              setKeywordSearch(
                e.target.value
              )
            }
          />

          <select
            value={keywordIntent}
            onChange={(e) =>
              setKeywordIntent(
                e.target.value
              )
            }
          >
            <option value="">
              All intents
            </option>

            {SEO_INTENTS.map(
              (intent) => (
                <option
                  key={intent}
                  value={intent}
                >
                  {capitalize(
                    intent
                  )}
                </option>
              )
            )}
          </select>
        </div>

        {keywordLoading ? (
          <p className="muted">
            Loading keyword library…
          </p>
        ) : !filteredKeywords.length ? (
          <div
            style={{
              padding:
                "28px 10px",
              textAlign:
                "center",
            }}
          >
            <p className="muted">
              No keywords found.
            </p>

            <button
              className="button button-dark small"
              type="button"
              onClick={() =>
                setKeywordEditing({
                  term: "",
                  intent:
                    "commercial",
                  target_scope:
                    "global",
                  priority: 50,
                  active: true,
                  notes: "",
                })
              }
            >
              <Plus size={14} /> Add your
              first keyword
            </button>
          </div>
        ) : (
          <div
            className="admin-table-wrap"
            style={{
              marginTop: 14,
            }}
          >
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th>Intent</th>
                  <th>Target</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {filteredKeywords.map(
                  (item) => (
                    <tr
                      key={
                        item.keyword_id
                      }
                    >
                      <td>
                        <b>
                          {item.term}
                        </b>

                        {item.notes && (
                          <small
                            style={{
                              display:
                                "block",
                              marginTop:
                                3,
                            }}
                          >
                            {item.notes}
                          </small>
                        )}
                      </td>

                      <td>
                        <span className="pill">
                          {capitalize(
                            item.intent
                          )}
                        </span>
                      </td>

                      <td>
                        {item.target_scope ||
                          "global"}
                      </td>

                      <td>
                        {item.priority}
                      </td>

                      <td>
                        <span
                          className={
                            item.active
                              ? "pill pill-published"
                              : "pill pill-draft"
                          }
                        >
                          {item.active
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>

                      <td className="actions">
                        <button
                          type="button"
                          onClick={() =>
                            setKeywordEditing(
                              item
                            )
                          }
                          aria-label="Edit keyword"
                        >
                          <Pencil
                            size={14}
                          />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            deleteKeyword(
                              item
                            )
                          }
                          aria-label="Delete keyword"
                        >
                          <Trash2
                            size={14}
                          />
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {keywordEditing && (
        <div
          className="admin-modal"
          role="dialog"
          aria-modal
        >
          <form
            className="admin-modal-inner"
            onSubmit={saveKeyword}
          >
            <header>
              <h2>
                {keywordEditing.keyword_id
                  ? "Edit SEO keyword"
                  : "Add SEO keyword"}
              </h2>

              <button
                type="button"
                onClick={() =>
                  setKeywordEditing(
                    null
                  )
                }
              >
                <X size={18} />
              </button>
            </header>

            <div className="form-grid">
              <label className="full">
                Keyword / search term
                <input
                  required
                  value={
                    keywordEditing.term ||
                    ""
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        term: e.target.value,
                      })
                    )
                  }
                  placeholder="e.g. authentic Kashmiri Pashmina shawl"
                />
              </label>

              <label>
                Search intent
                <select
                  value={
                    keywordEditing.intent ||
                    "commercial"
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        intent:
                          e.target.value,
                      })
                    )
                  }
                >
                  {SEO_INTENTS.map(
                    (intent) => (
                      <option
                        key={intent}
                        value={intent}
                      >
                        {capitalize(
                          intent
                        )}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                Target page
                <select
                  value={
                    keywordEditing.target_scope ||
                    "global"
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        target_scope:
                          e.target.value,
                      })
                    )
                  }
                >
                  <option value="global">
                    Global
                  </option>
                  {SEO_SECTIONS.map(
                    (section) => (
                      <option
                        key={`${section.type}:${section.key}`}
                        value={
                          section.key
                        }
                      >
                        {section.label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                Priority (0–100)
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={
                    keywordEditing.priority ??
                    50
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        priority:
                          Number(
                            e.target.value
                          ),
                      })
                    )
                  }
                />
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={
                    keywordEditing.active !==
                    false
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        active:
                          e.target.checked,
                      })
                    )
                  }
                />
                Active keyword
              </label>

              <label className="full">
                Notes
                <textarea
                  rows={4}
                  value={
                    keywordEditing.notes ||
                    ""
                  }
                  onChange={(e) =>
                    setKeywordEditing(
                      (current) => ({
                        ...current,
                        notes:
                          e.target.value,
                      })
                    )
                  }
                  placeholder="Optional note about why this keyword matters."
                />
              </label>
            </div>

            <footer>
              <button
                type="button"
                className="button"
                onClick={() =>
                  setKeywordEditing(
                    null
                  )
                }
              >
                Cancel
              </button>

              <button
                className="button button-dark"
                type="submit"
              >
                Save keyword{" "}
                <ArrowRight size={16} />
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function SEOEditor({
  editing,
  loading,
  saving,
  activeSection,
  updateField,
  addListItem,
  removeListItem,
  saveSEO,
  resetSEO,
}) {
  const [keywordInput, setKeywordInput] =
    useState("");

  const [phraseInput, setPhraseInput] =
    useState("");

  if (loading) {
    return (
      <div className="admin-card">
        <p className="muted">
          Loading SEO settings…
        </p>
      </div>
    );
  }

  const keywords =
    Array.isArray(editing.keywords)
      ? editing.keywords
      : [];

  const phrases =
    Array.isArray(
      editing.search_phrases
    )
      ? editing.search_phrases
      : [];

  const addKeywordFromInput = () => {
    addListItem(
      "keywords",
      keywordInput
    );
    setKeywordInput("");
  };

  const addPhraseFromInput = () => {
    addListItem(
      "search_phrases",
      phraseInput
    );
    setPhraseInput("");
  };

  return (
    <div>
      <div className="admin-card">
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <span className="eyebrow">
              {activeSection.type ===
              "global"
                ? "Website default"
                : "Page SEO"}
            </span>

            <h2
              style={{
                margin:
                  "4px 0 6px",
              }}
            >
              {activeSection.label}
            </h2>

            <p className="muted">
              {activeSection.description}
            </p>
          </div>

          <span className="pill">
            {editing.robots ||
              "index, follow"}
          </span>
        </div>

        <div className="form-grid">
          <label className="full">
            SEO title
            <input
              value={editing.title || ""}
              maxLength={70}
              onChange={(e) =>
                updateField(
                  "title",
                  e.target.value
                )
              }
              placeholder="Clear, search-focused page title"
            />

            <small
              className={
                editing.title?.length >
                60
                  ? "warn"
                  : "muted"
              }
            >
              {editing.title?.length ||
                0}
              /70 characters
            </small>
          </label>

          <label className="full">
            Meta description
            <textarea
              rows={4}
              maxLength={320}
              value={
                editing.description ||
                ""
              }
              onChange={(e) =>
                updateField(
                  "description",
                  e.target.value
                )
              }
              placeholder="Explain clearly what this page offers and why someone should visit."
            />

            <small
              className={
                editing.description
                  ?.length > 300
                  ? "warn"
                  : "muted"
              }
            >
              {editing.description
                ?.length || 0}
              /320 characters
            </small>
          </label>

          <label className="full">
            Focus keyword
            <input
              value={
                editing.focus_keyword ||
                ""
              }
              onChange={(e) =>
                updateField(
                  "focus_keyword",
                  e.target.value
                )
              }
              placeholder="Example: Kashmiri Pashmina shawls"
            />

            <small className="muted">
              The main search topic this
              page is intended to satisfy.
            </small>
          </label>

          <div className="full">
            <label>
              Related keywords
            </label>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                margin:
                  "8px 0 10px",
              }}
            >
              {keywords.map(
                (keyword, index) => (
                  <span
                    key={`${keyword}-${index}`}
                    style={{
                      display:
                        "inline-flex",
                      alignItems:
                        "center",
                      gap: 5,
                      border:
                        "1px solid rgba(0,0,0,.12)",
                      borderRadius:
                        999,
                      padding:
                        "5px 9px",
                      fontSize: 13,
                    }}
                  >
                    {keyword}

                    <button
                      type="button"
                      onClick={() =>
                        removeListItem(
                          "keywords",
                          index
                        )
                      }
                      style={{
                        border: 0,
                        background:
                          "transparent",
                        cursor:
                          "pointer",
                        padding: 0,
                      }}
                      aria-label={`Remove ${keyword}`}
                    >
                      ×
                    </button>
                  </span>
                )
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
              }}
            >
              <input
                value={keywordInput}
                onChange={(e) =>
                  setKeywordInput(
                    e.target.value
                  )
                }
                placeholder="Add a related keyword"
                onKeyDown={(e) => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    e.preventDefault();
                    addKeywordFromInput();
                  }
                }}
              />

              <button
                type="button"
                className="button button-dark small"
                onClick={
                  addKeywordFromInput
                }
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>

          <div className="full">
            <label>
              Search phrases / real search
              variations
            </label>

            <p className="muted">
              Add natural phrases people
              might actually type into
              Google. Keep them relevant to
              this page.
            </p>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                margin:
                  "8px 0 10px",
              }}
            >
              {phrases.map(
                (phrase, index) => (
                  <span
                    key={`${phrase}-${index}`}
                    style={{
                      display:
                        "inline-flex",
                      alignItems:
                        "center",
                      gap: 5,
                      border:
                        "1px solid rgba(0,0,0,.12)",
                      borderRadius:
                        999,
                      padding:
                        "5px 9px",
                      fontSize: 13,
                    }}
                  >
                    {phrase}

                    <button
                      type="button"
                      onClick={() =>
                        removeListItem(
                          "search_phrases",
                          index
                        )
                      }
                      style={{
                        border: 0,
                        background:
                          "transparent",
                        cursor:
                          "pointer",
                        padding: 0,
                      }}
                      aria-label={`Remove ${phrase}`}
                    >
                      ×
                    </button>
                  </span>
                )
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
              }}
            >
              <input
                value={phraseInput}
                onChange={(e) =>
                  setPhraseInput(
                    e.target.value
                  )
                }
                placeholder="Example: where to buy authentic Kashmiri Pashmina"
                onKeyDown={(e) => {
                  if (
                    e.key ===
                    "Enter"
                  ) {
                    e.preventDefault();
                    addPhraseFromInput();
                  }
                }}
              />

              <button
                type="button"
                className="button button-dark small"
                onClick={
                  addPhraseFromInput
                }
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>

          <label className="full">
            Default image ALT text
            <input
              value={
                editing.image_alt_default ||
                ""
              }
              onChange={(e) =>
                updateField(
                  "image_alt_default",
                  e.target.value
                )
              }
              placeholder="Descriptive text for the page's main image"
            />

            <small className="muted">
              Describe the actual image.
              Don't stuff keywords unnaturally.
            </small>
          </label>
        </div>
      </div>

      <div
        className="admin-card"
        style={{
          marginTop: 16,
        }}
      >
        <h3>Google & social sharing</h3>

        <div className="form-grid">
          <label className="full">
            Social / Open Graph title
            <input
              value={
                editing.og_title || ""
              }
              onChange={(e) =>
                updateField(
                  "og_title",
                  e.target.value
                )
              }
              placeholder="Optional — defaults to SEO title"
            />
          </label>

          <label className="full">
            Social / Open Graph description
            <textarea
              rows={3}
              value={
                editing.og_description ||
                ""
              }
              onChange={(e) =>
                updateField(
                  "og_description",
                  e.target.value
                )
              }
              placeholder="Optional — defaults to meta description"
            />
          </label>

          <label className="full">
            Social sharing image URL
            <input
              value={
                editing.og_image || ""
              }
              onChange={(e) =>
                updateField(
                  "og_image",
                  e.target.value
                )
              }
              placeholder="Optional image URL"
            />
          </label>
        </div>
      </div>

      <div
        className="admin-card"
        style={{
          marginTop: 16,
        }}
      >
        <h3>Technical SEO</h3>

        <div className="form-grid">
          <label className="full">
            Canonical URL
            <input
              value={
                editing.canonical_url ||
                ""
              }
              onChange={(e) =>
                updateField(
                  "canonical_url",
                  e.target.value
                )
              }
              placeholder="https://www.gmkhandicrafts.in/..."
            />

            <small className="muted">
              Use this only when you need
              this page to have a specific
              canonical address.
            </small>
          </label>

          <label>
            Search engine visibility
            <select
              value={
                editing.robots ||
                "index, follow"
              }
              onChange={(e) =>
                updateField(
                  "robots",
                  e.target.value
                )
              }
            >
              {SEO_ROBOTS.map(
                (value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value}
                  </option>
                )
              )}
            </select>
          </label>
        </div>
      </div>

      <div
        className="admin-card"
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent:
            "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="button"
          onClick={resetSEO}
        >
          Reset to default
        </button>

        <button
          type="button"
          className="button button-dark"
          onClick={saveSEO}
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : "Save SEO settings"}{" "}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function capitalize(value) {
  if (!value) return "";

  return (
    value.charAt(0).toUpperCase() +
    value.slice(1)
  );
}

function Admins() {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");

  const refresh = () =>
    apiFetch("/admins")
      .then(setList)
      .catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const invite = async (e) => {
    e.preventDefault();

    try {
      await apiFetch("/admins/invite", {
        method: "POST",
        body: JSON.stringify({
          email,
          role,
        }),
      });

      toast.success(
        "Admin invited / updated"
      );

      setEmail("");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (u) => {
    if (
      !window.confirm(
        `Remove admin access from ${u.email}?`
      )
    ) {
      return;
    }

    try {
      await apiFetch(
        `/admins/${u.user_id}`,
        {
          method: "DELETE",
        }
      );

      toast.success("Removed");
      refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <span className="eyebrow">
            Access control
          </span>
          <h1>Admins</h1>
        </div>
      </header>

      <div className="admin-card">
        <h3>
          Current admins & staff
        </h3>

        {list.map((u) => (
          <div
            className="row"
            key={u.user_id}
          >
            <span>
              <b>{u.email}</b> ┬и{" "}
              <small>
                {u.full_name || "—"}
              </small>
            </span>

            <span>
              <span className="pill">
                {u.role}
              </span>

              {u.role !== "owner" && (
                <button
                  className="link"
                  onClick={() =>
                    remove(u)
                  }
                  data-testid={`remove-admin-${u.email}`}
                >
                  Remove
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      <form
        className="admin-card"
        onSubmit={invite}
      >
        <h3>
          Add admin / staff
        </h3>

        <p className="muted">
          On their first Google sign-in
          with this email they'll receive
          the chosen role.
        </p>

        <div className="form-grid">
          <label>
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) =>
                setEmail(
                  e.target.value
                )
              }
              data-testid="invite-email"
            />
          </label>

          <label>
            Role
            <select
              value={role}
              onChange={(e) =>
                setRole(
                  e.target.value
                )
              }
              data-testid="invite-role"
            >
              <option value="admin">
                Admin (full access)
              </option>

              <option value="staff">
                Staff (orders only)
              </option>
            </select>
          </label>
        </div>

        <button
          className="button button-dark"
          data-testid="invite-submit"
        >
          Add admin{" "}
          <ArrowRight size={16} />
        </button>
      </form>
    </div>
  );
}
